import {
  type StackType,
  fetchEnvironments,
  fetchStacks,
  getStackType,
  selectStacks,
} from "@app/deploy";
import { selectIsUserAnyOwner } from "@app/deploy/permission";
import {
  MAX_SECURITY_CONTROL_SCOPE,
  fetchCustomerControls,
  fetchGlobalControls,
  selectSecurityControlAggregates,
  selectSecurityControlDefaultScope,
  selectSecurityControlEnvironments,
  selectSecurityControlRequiresDedicatedStack,
} from "@app/deploy/security-controls";
import {
  selectOrganizationById,
  selectOrganizationSelectedId,
} from "@app/organizations";
import { useLoader, useSelector } from "@app/react";
import { useQuery } from "@app/react";
import { fetchRoles } from "@app/roles";
import { AppSidebarLayout } from "@app/ui/layouts/app-sidebar-layout";
import { Button } from "@app/ui/shared/button";
import {
  ExportBanner,
  type ExportBannerStatus,
} from "@app/ui/shared/export-banner";
import { IconDownload, IconExternalLink, IconInfo } from "@app/ui/shared/icons";
import { Loading, LoadingSpinner } from "@app/ui/shared/loading";
import {
  CONTROLS_EVENTS,
  ControlFilterBar,
  ControlTable,
  DedicatedStackBanner,
  FrameworkMetrics,
  NoEnvironments,
  type ScopeEnvironment,
  controlsEvent,
  isControlsSortValidForScope,
  resolveControlsSortFromSearchParams,
  resolveControlsTableInitialSort,
} from "@app/ui/shared/security-controls";
import { Tooltip } from "@app/ui/shared/tooltip";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { PrintControlsReportSurface } from "./print-controls";

const toStableKey = (values: string[]) => [...values].sort().join(",");

/** Filter params preserved when opening a control detail view. */
const CONTROL_DETAIL_SEARCH_PARAMS = [
  "scope",
  "framework",
  "category",
] as const;

const FRAMEWORK_ALL_HANDLE = "all";
const PRINT_EXPORT_TIMEOUT_MS = 30_000;
const PRINT_CLEANUP_FALLBACK_MS = 120_000;
const emptySecurityControlsViewModel = {
  controls: [],
  frameworks: {},
  categories: [],
};

const stackTypeLabels: Record<
  StackType,
  "Dedicated" | "Shared" | "Self-Hosted"
> = {
  dedicated: "Dedicated",
  shared: "Shared",
  self_hosted: "Self-Hosted",
};

const parseFrameworkSelection = (value: string | null): string[] => {
  if (!value) return [FRAMEWORK_ALL_HANDLE];
  const parsed = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return parsed.length > 0 ? parsed : [FRAMEWORK_ALL_HANDLE];
};

const normalizeFrameworkSelection = (
  nextValues: string[],
  prevValues: string[],
): string[] => {
  const deduped = Array.from(new Set(nextValues));
  const hasAll = deduped.includes(FRAMEWORK_ALL_HANDLE);
  const specific = deduped.filter((value) => value !== FRAMEWORK_ALL_HANDLE);

  if (!hasAll) {
    return specific.length > 0 ? specific : [FRAMEWORK_ALL_HANDLE];
  }

  if (specific.length === 0) {
    return [FRAMEWORK_ALL_HANDLE];
  }

  // If "all" was already selected, a specific selection replaces it.
  if (prevValues.includes(FRAMEWORK_ALL_HANDLE)) {
    return specific;
  }

  // Otherwise selecting "all" clears specifics.
  return [FRAMEWORK_ALL_HANDLE];
};

const ControlsPdfPrintJob = ({
  scopeIds,
  categoryParams,
  frameworkParams,
  onPrinted,
  onError,
}: {
  scopeIds: string[];
  categoryParams: string[];
  frameworkParams: string[];
  onPrinted: () => void;
  onError: () => void;
}) => {
  const hasStartedPrint = useRef(false);
  const hasFinished = useRef(false);
  const firstFrameId = useRef<number | null>(null);
  const secondFrameId = useRef<number | null>(null);
  const readinessTimeoutId = useRef<number | null>(null);
  const cleanupFallbackId = useRef<number | null>(null);
  const finishListener = useRef<(() => void) | null>(null);

  useEffect(() => {
    document.body.classList.add("controls-report-printing");
    return () => {
      document.body.classList.remove("controls-report-printing");
    };
  }, []);

  const clearPrintSideEffects = useCallback(() => {
    if (firstFrameId.current !== null) {
      window.cancelAnimationFrame(firstFrameId.current);
      firstFrameId.current = null;
    }
    if (secondFrameId.current !== null) {
      window.cancelAnimationFrame(secondFrameId.current);
      secondFrameId.current = null;
    }
    if (readinessTimeoutId.current !== null) {
      window.clearTimeout(readinessTimeoutId.current);
      readinessTimeoutId.current = null;
    }
    if (cleanupFallbackId.current !== null) {
      window.clearTimeout(cleanupFallbackId.current);
      cleanupFallbackId.current = null;
    }
    if (finishListener.current !== null) {
      window.removeEventListener("afterprint", finishListener.current);
      window.removeEventListener("focus", finishListener.current);
      finishListener.current = null;
    }
  }, []);

  const finishWithSuccess = useCallback(() => {
    if (hasFinished.current) return;
    hasFinished.current = true;
    clearPrintSideEffects();
    onPrinted();
  }, [clearPrintSideEffects, onPrinted]);

  const finishWithError = useCallback(() => {
    if (hasFinished.current) return;
    hasFinished.current = true;
    clearPrintSideEffects();
    onError();
  }, [clearPrintSideEffects, onError]);

  const handleReady = useCallback(() => {
    if (hasStartedPrint.current) return;
    hasStartedPrint.current = true;
    if (readinessTimeoutId.current !== null) {
      window.clearTimeout(readinessTimeoutId.current);
      readinessTimeoutId.current = null;
    }

    firstFrameId.current = window.requestAnimationFrame(() => {
      firstFrameId.current = null;
      secondFrameId.current = window.requestAnimationFrame(() => {
        secondFrameId.current = null;
        cleanupFallbackId.current = window.setTimeout(
          finishWithError,
          PRINT_CLEANUP_FALLBACK_MS,
        );
        const finishAndClear = () => finishWithSuccess();
        finishListener.current = finishAndClear;

        window.addEventListener("afterprint", finishAndClear, { once: true });
        window.addEventListener("focus", finishAndClear, { once: true });

        try {
          window.print();
        } catch {
          finishWithError();
        }
      });
    });
  }, [finishWithError, finishWithSuccess]);

  useEffect(() => {
    readinessTimeoutId.current = window.setTimeout(() => {
      readinessTimeoutId.current = null;
      if (!hasStartedPrint.current) {
        finishWithError();
      }
    }, PRINT_EXPORT_TIMEOUT_MS);

    return clearPrintSideEffects;
  }, [clearPrintSideEffects, finishWithError]);

  return createPortal(
    <div className="controls-report-print-source" aria-hidden="true">
      <PrintControlsReportSurface
        scopeIds={scopeIds}
        categoryParams={categoryParams}
        frameworkParams={frameworkParams}
        onReady={handleReady}
      />
    </div>,
    document.body,
  );
};

export const SecurityControlsDashboardPage = () => {
  const { controlId: selectedControlId } = useParams<{
    controlId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = useSelector(selectOrganizationSelectedId);
  const organization = useSelector((state) =>
    selectOrganizationById(state, { id: orgId }),
  );
  const envSets = useSelector((state) =>
    selectSecurityControlEnvironments(state, { orgId }),
  );
  const defaultScope = useSelector((state) =>
    selectSecurityControlDefaultScope(state, { orgId }),
  );
  const requiresDedicatedStack = useSelector((state) =>
    selectSecurityControlRequiresDedicatedStack(state, { orgId }),
  );
  const canCreateEnvironment = useSelector(selectIsUserAnyOwner);

  useQuery(fetchGlobalControls());
  useQuery(fetchEnvironments());
  useQuery(fetchStacks());
  useQuery(fetchRoles({ orgId }));
  const stacks = useSelector(selectStacks);

  const scopeParam = searchParams.get("scope") || "";
  const frameworkParam = searchParams.get("framework");
  const appliedFrameworks = parseFrameworkSelection(frameworkParam);
  const categoryParam = searchParams.get("category") || "";
  const appliedCategories = categoryParam
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!orgId) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;

    // Only set default scope if the parameter doesn't exist at all (not if it's empty)
    if (!searchParams.has("scope") && defaultScope.length > 0) {
      next.set("scope", defaultScope.join(","));
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [defaultScope, scopeParam, searchParams, setSearchParams, orgId]);

  const scopeFromParams = useMemo(() => {
    if (!orgId) return [];
    if (!scopeParam) {
      return defaultScope;
    }
    return scopeParam
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }, [scopeParam, defaultScope, orgId]);

  const scopeIds = scopeFromParams.slice(0, MAX_SECURITY_CONTROL_SCOPE);
  const scopeIdsKey = scopeIds.join(",");

  // fetch global controls once at mount (above) and customer controls for scope
  useQuery(fetchCustomerControls({ orgId, accountIds: scopeIds }));

  const globalControlsLoader = useLoader(fetchGlobalControls);
  const customerControlsLoader = useLoader(fetchCustomerControls);
  const envsLoader = useLoader(fetchEnvironments);
  const securityAggregatesProps = useMemo(
    () => ({ orgId, scopeIds }),
    [orgId, scopeIdsKey],
  );

  const viewModel = useSelector((state) => {
    if (!orgId || scopeIds.length === 0) {
      return emptySecurityControlsViewModel;
    }
    return selectSecurityControlAggregates(state, securityAggregatesProps);
  });

  const scopeEnvironments: ScopeEnvironment[] = useMemo(() => {
    return scopeIds
      .map((id): ScopeEnvironment | undefined => {
        const env = envSets.all.find((candidate) => candidate.id === id);
        if (!env) return undefined;
        const stack = stacks[env.stackId];
        const stackType = stack == null ? undefined : getStackType(stack);
        return {
          id: env.id,
          handle: env.handle,
          type: env.type,
          stackId: env.stackId,
          ...(stackType ? { stackType } : {}),
        };
      })
      .filter((env): env is ScopeEnvironment => Boolean(env));
  }, [envSets.all, scopeIds, stacks]);

  const isAggregateScope = scopeEnvironments.length > 1;
  const controlsTableInitialSort = useMemo(
    () =>
      resolveControlsTableInitialSort(
        searchParams,
        isAggregateScope,
        scopeEnvironments,
      ),
    [searchParams, isAggregateScope, scopeEnvironments],
  );

  // Migrate legacy sort query params (sort, nameDir, statusDir) to ag-grid controls* params.
  useEffect(() => {
    if (!searchParams.has("sort")) return;

    const resolved = resolveControlsSortFromSearchParams(
      searchParams,
      isAggregateScope,
      scopeEnvironments,
    );
    if (!resolved) return;

    const next = new URLSearchParams(searchParams);
    next.set("controlsSortBy", resolved.field);
    next.set("controlsSortDir", resolved.direction);
    next.delete("sort");
    next.delete("nameDir");
    next.delete("statusDir");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isAggregateScope, scopeEnvironments]);

  // Drop table sort params that do not match the current column layout (e.g. after scope change).
  useEffect(() => {
    const sortBy = searchParams.get("controlsSortBy");
    if (
      !sortBy ||
      isControlsSortValidForScope(sortBy, isAggregateScope, scopeEnvironments)
    ) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("controlsSortBy");
        next.delete("controlsSortDir");
        next.delete("controlsPage");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, isAggregateScope, scopeEnvironments]);

  const frameworkSelectedKey = toStableKey(appliedFrameworks);
  const defaultScopeIds = defaultScope.slice(0, MAX_SECURITY_CONTROL_SCOPE);
  const defaultScopeKey = defaultScopeIds.join(",");
  const appliedScopeKey = scopeIds.join(",");

  const [stagedScopeIds, setStagedScopeIds] = useState<string[]>(scopeIds);
  const [stagedCategories, setStagedCategories] = useState(appliedCategories);
  const [stagedFrameworks, setStagedFrameworks] = useState(appliedFrameworks);

  useEffect(() => {
    setStagedScopeIds(appliedScopeKey ? appliedScopeKey.split(",") : []);
    setStagedCategories(appliedCategories);
  }, [appliedScopeKey, categoryParam]);

  useEffect(() => {
    setStagedFrameworks(appliedFrameworks);
  }, [frameworkSelectedKey]);

  const filteredControls = useMemo(() => {
    let controls = viewModel.controls;
    if (appliedCategories.length > 0) {
      controls = controls.filter((control) =>
        appliedCategories.includes(control.globalControl.area),
      );
    }
    if (!appliedFrameworks.includes(FRAMEWORK_ALL_HANDLE)) {
      controls = controls.filter((control) =>
        control.globalControl.frameworkHandles.some((framework) =>
          appliedFrameworks.includes(framework),
        ),
      );
    }
    return controls;
  }, [viewModel.controls, categoryParam, frameworkSelectedKey]);

  const updateSearchParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const handleCategoriesChange = (values: string[]) => {
    setStagedCategories(values);
  };

  const handleFrameworkSelect = (value: string) => {
    const normalized = normalizeFrameworkSelection(
      appliedFrameworks.includes(value)
        ? appliedFrameworks.filter((framework) => framework !== value)
        : [...appliedFrameworks, value],
      appliedFrameworks,
    );
    updateSearchParam(
      "framework",
      normalized.includes(FRAMEWORK_ALL_HANDLE)
        ? FRAMEWORK_ALL_HANDLE
        : normalized.join(","),
    );
  };

  const handleFrameworksChange = (values: string[]) => {
    const normalized = normalizeFrameworkSelection(values, stagedFrameworks);
    setStagedFrameworks(normalized);
  };

  const handleScopeSubmit = (nextScope: string[]) => {
    setStagedScopeIds(nextScope.slice(0, MAX_SECURITY_CONTROL_SCOPE));
  };
  const handleApplyFilters = () => {
    const nextScopeEnvironments = stagedScopeIds
      .map((id) => envSets.all.find((env) => env.id === id))
      .filter((env): env is NonNullable<typeof env> => Boolean(env))
      .map((env) => ({
        id: env.id,
        handle: env.handle,
        type: env.type,
        stackId: env.stackId,
      }));
    const nextIsAggregateScope = nextScopeEnvironments.length > 1;

    const next = new URLSearchParams(searchParams);
    next.set(
      "scope",
      stagedScopeIds.length > 0 ? stagedScopeIds.join(",") : "",
    );
    if (stagedCategories.length > 0) {
      next.set("category", stagedCategories.join(","));
    } else {
      next.delete("category");
    }
    if (
      stagedFrameworks.length === 0 ||
      stagedFrameworks.includes(FRAMEWORK_ALL_HANDLE)
    ) {
      next.delete("framework");
    } else {
      next.set("framework", stagedFrameworks.join(","));
    }

    const sortBy = next.get("controlsSortBy");
    if (
      sortBy &&
      !isControlsSortValidForScope(
        sortBy,
        nextIsAggregateScope,
        nextScopeEnvironments,
      )
    ) {
      next.delete("controlsSortBy");
      next.delete("controlsSortDir");
      next.delete("controlsPage");
    }

    setSearchParams(next);
  };
  const handleResetFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.set(
      "scope",
      defaultScopeIds.length > 0 ? defaultScopeIds.join(",") : "",
    );
    next.delete("category");
    next.delete("framework");
    setStagedScopeIds(defaultScopeIds);
    setStagedCategories([]);
    setStagedFrameworks([FRAMEWORK_ALL_HANDLE]);
    setSearchParams(next);
  };

  const isInitialLoading =
    globalControlsLoader.isInitialLoading ||
    customerControlsLoader.isInitialLoading;
  const hasNoEnvironments = envSets.all.length === 0;
  const hasPendingChanges =
    stagedScopeIds.join(",") !== appliedScopeKey ||
    toStableKey(stagedCategories) !== toStableKey(appliedCategories) ||
    toStableKey(stagedFrameworks) !== toStableKey(appliedFrameworks);
  const hasActiveFilters =
    appliedScopeKey !== defaultScopeKey ||
    !appliedFrameworks.includes(FRAMEWORK_ALL_HANDLE) ||
    appliedCategories.length > 0;
  const environmentOptions = useMemo(() => {
    const selectedSet = new Set(stagedScopeIds);
    const allEnvs = [...envSets.production, ...envSets.development];
    return allEnvs.map((env) => {
      const isSelected = selectedSet.has(env.id);
      const isDisabled =
        !isSelected && stagedScopeIds.length >= MAX_SECURITY_CONTROL_SCOPE;
      const stack = stacks[env.stackId];
      const stackTypeLabel =
        stack == null ? undefined : stackTypeLabels[getStackType(stack)];
      return {
        value: env.id,
        label: env.handle,
        disabled: isDisabled,
        stackType: stackTypeLabel,
      };
    });
  }, [envSets.production, envSets.development, stagedScopeIds, stacks]);
  const frameworkOptions = useMemo(() => {
    const order = [
      FRAMEWORK_ALL_HANDLE,
      "hipaa-required",
      "hipaa-addressable",
      "hitrust-inheritable",
    ];
    const options = Object.values(viewModel.frameworks).map((framework) => ({
      value: framework.handle,
      label: framework.name,
    }));
    if (!options.some((option) => option.value === FRAMEWORK_ALL_HANDLE)) {
      options.unshift({ value: FRAMEWORK_ALL_HANDLE, label: "All Controls" });
    }
    return options.sort((a, b) => {
      const aIndex = order.indexOf(a.value);
      const bIndex = order.indexOf(b.value);
      return (
        (aIndex === -1 ? order.length : aIndex) -
        (bIndex === -1 ? order.length : bIndex)
      );
    });
  }, [viewModel.frameworks]);

  const getControlLink = useCallback(
    (id: string) => {
      const next = new URLSearchParams();
      for (const key of CONTROL_DETAIL_SEARCH_PARAMS) {
        const value = searchParams.get(key);
        if (value) {
          next.set(key, value);
        }
      }
      const qs = next.toString();
      return qs
        ? `/controls/${id}/implementation?${qs}`
        : `/controls/${id}/implementation`;
    },
    [searchParams],
  );

  const [pdfExportStatus, setPdfExportStatus] =
    useState<ExportBannerStatus>("idle");
  const handlePdfExport = useCallback(() => {
    controlsEvent(CONTROLS_EVENTS.EXPORT_PDF_REPORT_CLICK, {
      orgId,
      orgName: organization.name,
    });
    setPdfExportStatus("exporting");
  }, [orgId, organization.name]);
  const handlePdfExportCancel = useCallback(() => {
    setPdfExportStatus("idle");
  }, []);
  const handlePdfExportError = useCallback(() => {
    setPdfExportStatus("error");
  }, []);

  // Show loading if: environments are loading OR roles are loading
  // (roles are needed to determine access via permissions)
  const rolesLoader = useLoader(fetchRoles({ orgId }));
  const isEnvironmentsLoading =
    envsLoader.isInitialLoading || rolesLoader.isInitialLoading;

  if (!orgId) {
    return (
      <AppSidebarLayout>
        <LoadingSpinner />
      </AppSidebarLayout>
    );
  }
  if (isEnvironmentsLoading) {
    return (
      <AppSidebarLayout>
        <div className="mt-4">
          <Loading text="Loading ..." />
        </div>
      </AppSidebarLayout>
    );
  }
  if (hasNoEnvironments) {
    return (
      <AppSidebarLayout>
        <NoEnvironments
          organizationName={organization.name || "your organization"}
          canCreateEnvironment={canCreateEnvironment}
        />
      </AppSidebarLayout>
    );
  }

  return (
    <AppSidebarLayout>
      <div
        className={cn(
          "flex flex-col gap-8 min-w-0 overflow-x-hidden w-full max-w-full",
        )}
        style={{ contain: "inline-size" }}
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-semibold text-gray-900">
                  Security &amp; Compliance
                </h1>
                <Tooltip text="This dashboard shows the security and compliance controls for your infrastructure running on Aptible. Control status is evaluated automatically every 24 hours.">
                  <IconInfo variant="sm" className="text-gray-500" />
                </Tooltip>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://trust.aptible.com/"
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  controlsEvent(CONTROLS_EVENTS.TRUST_CENTER_CLICK, {
                    orgId,
                    orgName: organization.name,
                  })
                }
              >
                <Button
                  variant="white"
                  size="md"
                  className="w-fit whitespace-nowrap"
                >
                  <IconExternalLink variant="sm" className="mr-1" aria-hidden />
                  Trust Center
                </Button>
              </a>
              <Button
                variant="primary"
                size="md"
                onClick={handlePdfExport}
                disabled={pdfExportStatus === "exporting"}
                className="w-fit whitespace-nowrap"
              >
                <IconDownload variant="sm" className="mr-1" aria-hidden />
                Download Report
              </Button>
            </div>
          </div>
        </div>

        {pdfExportStatus !== "idle" ? (
          <ExportBanner
            status={pdfExportStatus}
            onCancel={handlePdfExportCancel}
          />
        ) : null}

        {pdfExportStatus === "exporting" ? (
          <ControlsPdfPrintJob
            scopeIds={scopeIds}
            categoryParams={appliedCategories}
            frameworkParams={appliedFrameworks}
            onPrinted={handlePdfExportCancel}
            onError={handlePdfExportError}
          />
        ) : null}

        {requiresDedicatedStack ? <DedicatedStackBanner /> : null}

        <div
          className={cn(
            "relative z-20 w-full max-w-full min-w-0 overflow-visible",
          )}
        >
          <FrameworkMetrics
            metrics={viewModel.frameworks}
            selectedFrameworks={appliedFrameworks}
            isLoading={isInitialLoading}
            onToggle={handleFrameworkSelect}
          />

          <ControlFilterBar
            className="mt-4"
            environmentOptions={environmentOptions}
            environmentSelectionLimit={MAX_SECURITY_CONTROL_SCOPE}
            selectedEnvironmentIds={stagedScopeIds}
            onEnvironmentChange={handleScopeSubmit}
            frameworkOptions={frameworkOptions}
            selectedFrameworks={stagedFrameworks}
            onFrameworksChange={handleFrameworksChange}
            categories={viewModel.categories}
            selectedCategories={stagedCategories}
            onCategoriesChange={handleCategoriesChange}
            hasPendingChanges={hasPendingChanges}
            hasActiveFilters={hasActiveFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        </div>

        <div className="my-1 h-px w-full bg-gray-200" />

        <div className="relative z-0 w-full min-w-0 max-w-full overflow-x-auto">
          <ControlTable
            controls={filteredControls}
            scopeEnvironments={scopeEnvironments}
            isAggregateScope={isAggregateScope}
            isLoading={isInitialLoading}
            selectedControlId={selectedControlId}
            getControlLink={getControlLink}
            initialSort={controlsTableInitialSort}
          />
        </div>
      </div>
      <Outlet />
    </AppSidebarLayout>
  );
};
